// ===================================================================
// src/app/services/notifications.ts
//
// ELE-140 (Fase 2) — Notificações reais no sino do TopBar.
// Endpoints: apps/notifications/views.py (eleveia-api)
//   GET  /notifications/                → lista (filtrável por is_read/type)
//   POST /notifications/{id}/mark_read/ → marca uma como lida
//   POST /notifications/mark_all_read/  → marca todas como lidas
//   GET  /notifications/unread_count/   → contador de não lidas
// ===================================================================

import { api } from './api';
import type { PaginatedResponse } from './secretary';

export type NotificationType =
  | 'card_assigned'
  | 'comment_added'
  | 'request_approved'
  | 'request_rejected'
  | 'ticket_received'
  | 'ticket_resolved'
  | 'card_overdue'
  | 'card_due_soon'
  | 'announcement_urgent'
  | 'card_moved_cross_board'
  | 'chat_mention'
  | 'platform_feedback_resolved'
  | 'nps_alert_low_rating'
  | 'card_created_in_sector'
  | 'session_needs_human'
  | 'session_forwarded_to_sector'
  | 'session_tag_alert'
  | 'scheduled_message_alert'
  | 'critical_alert';

export interface NotificationItem {
  id: string;
  notification_type: NotificationType;
  notification_type_display: string;
  title: string;
  body: string;
  entity_type: string;
  entity_id: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

export interface NotificationPreferenceItem {
  notification_type: NotificationType;
  notification_type_display: string;
  in_app_enabled: boolean;
  email_enabled: boolean;
  lead_time_minutes: number | null;
  /** Se false, email_enabled não pode ser ligado pra esse tipo (backend rejeita) */
  supports_email: boolean;
  /** Se false, lead_time_minutes não se aplica (só card_due_soon hoje) */
  supports_lead_time: boolean;
}

export interface NotificationPreferenceUpdate {
  notification_type: NotificationType;
  in_app_enabled?: boolean;
  email_enabled?: boolean;
  lead_time_minutes?: number | null;
}

export const notificationsService = {
  list: (params?: { is_read?: boolean; page?: number }): Promise<PaginatedResponse<NotificationItem>> => {
    const query: Record<string, string> = {};
    if (params?.is_read !== undefined) query.is_read = String(params.is_read);
    if (params?.page !== undefined) query.page = String(params.page);
    return api.get<PaginatedResponse<NotificationItem>>('/notifications/', {
      params: Object.keys(query).length ? query : undefined,
    });
  },

  markRead: (id: string): Promise<{ is_read: true }> =>
    api.post<{ is_read: true }>(`/notifications/${id}/mark_read/`),

  markAllRead: (): Promise<{ marked_read: number }> =>
    api.post<{ marked_read: number }>('/notifications/mark_all_read/'),

  getUnreadCount: (): Promise<{ count: number }> =>
    api.get<{ count: number }>('/notifications/unread_count/'),

  /**
   * Resolve um card (entity_type='card') pelo pk sozinho, sem board_id
   * conhecido de antemão — usado para navegar da notificação até o board
   * certo. Mirror-aware no backend (apps.boards.services.assignment.
   * user_can_view_card): 403 se o usuário perdeu acesso ao setor do card.
   */
  resolveCard: (cardId: string): Promise<{ board: string; card_id: number }> =>
    api.get<{ board: string; card_id: number }>(`/boards/cards/${cardId}/resolve/`),

  getPreferences: (): Promise<NotificationPreferenceItem[]> =>
    api.get<NotificationPreferenceItem[]>('/notifications/preferences/'),

  updatePreference: (payload: NotificationPreferenceUpdate): Promise<NotificationPreferenceItem> =>
    api.patch<NotificationPreferenceItem>('/notifications/preferences/', payload),
};
